class CartComponent extends HTMLElement {
  constructor () {
    super();
    this.enableAjax = this.dataset.enableAjax === 'true';
    this.cartUrl = this.dataset.cartUrl;
    this.cartAddUrl = this.dataset.cartAddUrl;
    this.cartChangeUrl = this.dataset.cartChangeUrl;

    this.form = this.querySelector( '.cart-form' );
    this.loadingElement = this.querySelector( '.cart-loading' );
    this.updateButton = this.querySelector( '.cart-update-button' );
    this.checkoutButton = this.querySelector( '.checkout-button' );

    // Get currency from data attribute or existing cart prices
    this.currency = this.dataset.currency || this.getCurrencyFromCart();

    this.debounceTimer = null;
    this.pendingUpdates = new Map();

    // Track current confirmation dialog if any
    this.activeConfirmationDialog = null;

    this.init();
  }

  init () {
    // Always bind event listeners for basic functionality
    this.bindEvents();

    // Only proceed with AJAX-specific setup if enabled and form exists
    if ( !this.enableAjax || !this.form ) return;

    // Hide the update button when AJAX is enabled
    if ( this.updateButton ) {
      this.updateButton.style.display = 'none';
    }
  }

  bindEvents () {
    // Initialize original values for quantity inputs and setup input-specific listeners
    this.initializeInputListeners();

    // Quantity controls - these use event delegation, so no need to rebind
    if ( !this._eventsListenersBound ) {
      this.addEventListener( 'click', this.handleClick.bind( this ) );
      this.addEventListener( 'input', this.handleInput.bind( this ) );
      this.addEventListener( 'change', this.handleChange.bind( this ) );

      // Prevent form submission when AJAX is enabled
      if ( this.form ) {
        this.form.addEventListener( 'submit', this.handleFormSubmit.bind( this ) );
      }

      this._eventsListenersBound = true;
    }
  }

  initializeInputListeners () {
    // Initialize original values for quantity inputs
    this.querySelectorAll( '[data-item-quantity]' ).forEach( input => {
      input.dataset.originalValue = input.value;

      // Add event listeners for input value tracking
      input.addEventListener( 'mousedown', this.handleInputInteractionStart.bind( this ) );
      input.addEventListener( 'keydown', this.handleInputInteractionStart.bind( this ) );
      input.addEventListener( 'touchstart', this.handleInputInteractionStart.bind( this ) );

      // Special handler for browser's built-in number step buttons
      // This event captures when browser's up/down arrows are clicked
      input.addEventListener( 'input', ( e ) => {
        const value = parseInt( e.target.value ) || 0;
        if ( value === 0 ) {
          // Stop default input handling that would occur later
          e.stopImmediatePropagation();

          const cartItem = e.target.closest( '.cart-item' );
          // Show confirmation before proceeding
          this.showRemoveConfirmation( cartItem, e.target, 0 );
        }
      }, true ); // Use capturing phase to catch the event before our main handler
    } );
  }

  handleInputInteractionStart ( event ) {
    // Store the current value before user interaction begins
    // This ensures we have the latest value before any changes occur
    const input = event.target;
    if ( input.value && parseInt( input.value ) > 0 ) {
      input.dataset.originalValue = input.value;
    }
  }

  handleClick ( event ) {
    const target = event.target;

    // Use closest to handle clicks on child elements (like SVG icons)
    const quantityButton = target.closest( '[data-quantity-change]' );
    const removeButton = target.closest( '[data-remove-item]' );

    if ( quantityButton ) {
      event.preventDefault();
      this.handleQuantityChange( quantityButton );
    } else if ( removeButton ) {
      event.preventDefault();
      this.handleRemoveItem( removeButton );
    }
  }

  handleInput ( event ) {
    if ( event.target.matches( '[data-item-quantity]' ) ) {
      const input = event.target;
      const inputValue = parseInt( input.value ) || 0;
      const originalValue = parseInt( input.dataset.originalValue ) || 1;

      // Check if the value was reduced to zero using browser controls
      if ( inputValue === 0 ) {
        // Immediately check if we should show confirmation
        // Clear any pending debounce update
        clearTimeout( this.debounceTimer );

        // Show confirmation dialog
        const cartItem = input.closest( '.cart-item' );
        this.showRemoveConfirmation( cartItem, input, 0 );
        return;
      }

      this.debounceQuantityUpdate( input );
    }
  }

  handleChange ( event ) {
    if ( event.target.matches( '[data-item-quantity]' ) ) {
      const input = event.target;
      const cartItem = input.closest( '.cart-item' );
      const itemKey = cartItem.dataset.itemKey;
      const inputValue = parseInt( input.value ) || 0;
      const originalValue = parseInt( input.dataset.originalValue ) || 0;
      const pending = this.pendingUpdates.get( itemKey );

      // Ignore unchanged values to avoid duplicate updates
      if ( inputValue === originalValue ) return;

      // If there's a pending debounced update for this item, cancel and use the latest value immediately
      if ( pending && pending.timer ) {
        clearTimeout( pending.timer );
        pending.timer = null;
        this.pendingUpdates.set( itemKey, pending );
      }

      if ( pending && pending.inFlight ) {
        return;
      }

      // Check if value is zero (could be from down arrow or manual input)
      if ( inputValue === 0 ) {
        // Show confirmation before removing
        this.showRemoveConfirmation( cartItem, input, 0 );
        return;
      }

      this.updateQuantity( input );
    }
  }

  handleFormSubmit ( event ) {
    if ( !this.enableAjax ) return;

    // Allow checkout to proceed normally
    if ( event.submitter && event.submitter.name === 'checkout' ) {
      return;
    }

    // Prevent AJAX form updates since we handle them individually
    event.preventDefault();
  }

  handleQuantityChange ( button ) {
    const change = parseInt( button.dataset.quantityChange );
    const cartItem = button.closest( '.cart-item' );
    const quantityInput = cartItem.querySelector( '[data-item-quantity]' );

    if ( quantityInput ) {
      // Store original value if not already stored
      if ( !quantityInput.dataset.originalValue ) {
        quantityInput.dataset.originalValue = quantityInput.value;
      }

      const currentValue = parseInt( quantityInput.value ) || 0;
      const newValue = Math.max( 0, currentValue + change );

      // Check if this change would reduce quantity to 0 (by clicking the "-" button)
      if ( change < 0 && newValue === 0 ) {
        // Show confirmation before proceeding
        this.showRemoveConfirmation( cartItem, quantityInput, newValue );
        return;
      }

      quantityInput.value = newValue;
      this.updateQuantity( quantityInput );
    }
  }

  handleRemoveItem ( button ) {
    const cartItem = button.closest( '.cart-item' );
    const quantityInput = cartItem.querySelector( '[data-item-quantity]' );

    if ( quantityInput ) {
      // Show confirmation dialog before removing
      this.showRemoveConfirmation( cartItem, quantityInput, 0 );
    }
  }

  debounceQuantityUpdate ( input ) {
    const cartItem = input.closest( '.cart-item' );
    const itemKey = cartItem.dataset.itemKey;
    const inputValue = parseInt( input.value ) || 0;

    const pending = this.pendingUpdates.get( itemKey ) || {};
    if ( pending.timer ) {
      clearTimeout( pending.timer );
    }

    const timer = setTimeout( () => {
      this.pendingUpdates.set( itemKey, { timer: null, inFlight: false });

      // Check if user typed "0" to remove item
      if ( inputValue === 0 ) {
        this.showRemoveConfirmation( cartItem, input, 0 );
        return;
      }

      this.updateQuantity( input );
    }, 500 );

    this.pendingUpdates.set( itemKey, { timer, inFlight: false } );
  }

  updateQuantity ( input ) {
    if ( !this.enableAjax ) return;

    const cartItem = input.closest( '.cart-item' );
    const itemKey = cartItem.dataset.itemKey;
    const quantity = parseInt( input.value ) || 0;
    const pending = this.pendingUpdates.get( itemKey ) || {};

    if ( pending.timer ) {
      clearTimeout( pending.timer );
      pending.timer = null;
    }

    pending.inFlight = true;
    this.pendingUpdates.set( itemKey, pending );

    // Clear any existing error for this item
    this.clearItemError( cartItem );

    // Show loading state
    this.setItemLoading( cartItem, true );

    this.changeCartItem( itemKey, quantity )
      .then( response => {
        if ( !response.ok ) {
          return response.json().catch( () => ( {} ) ).then( errorData => {
            throw new Error( errorData.message || errorData.description || 'Failed to update cart' );
          } );
        }

        return response.json();
      } )
      .then( cart => {
        const updatedItem = cart.items.find( item => item.key === itemKey );
        
        // Determine if quantity was capped by stock
        const quantityCapped = updatedItem && updatedItem.quantity < quantity;
        const cappedQuantity = updatedItem ? updatedItem.quantity : quantity;

        // Refresh the entire cart component
        return this.refreshCartComponent().then( () => {
          // Show message if quantity was capped
          if ( quantityCapped ) {
            const updatedCartItem = this.querySelector( `[data-item-key="${itemKey}"]` );
            if ( updatedCartItem ) {
              this.showItemError( updatedCartItem, `Only ${cappedQuantity} item${cappedQuantity !== 1 ? 's' : ''} available in stock` );
            }
          }

          // Dispatch event with cart data
          document.dispatchEvent( new CustomEvent( 'cart:updated', {
            detail: {
              cart: cart,
              itemCount: cart.item_count,
              totalPrice: cart.total_price
            }
          } ) );
        } );
      } )
      .catch( error => {
        console.error( 'Cart update error:', error );

        let errorMessage = 'Failed to update cart. Please try again.';
        if ( error.message.includes( 'stock' ) || error.message.includes( 'available' ) || error.message.includes( 'inventory' ) ) {
          errorMessage = error.message;
        }

        const currentCartItem = this.querySelector( `[data-item-key="${itemKey}"]` );
        if ( currentCartItem ) {
          this.showItemError( currentCartItem, errorMessage );
        }
      } )
      .finally( () => {
        const currentCartItem = this.querySelector( `[data-item-key="${itemKey}"]` );
        if ( currentCartItem ) {
          this.setItemLoading( currentCartItem, false );
        }
        const pending = this.pendingUpdates.get( itemKey );
        if ( pending ) {
          pending.inFlight = false;
          this.pendingUpdates.set( itemKey, pending );
        }
      } );
  }

  changeCartItem ( itemKey, quantity ) {
    const body = JSON.stringify( {
      id: itemKey,
      quantity: quantity
    } );

    return fetch( `${this.cartChangeUrl}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    } );
  }

  refreshCartComponent () {
    return fetch( this.cartUrl )
      .then( response => {
        if ( !response.ok ) throw new Error( 'Failed to fetch cart' );
        return response.text();
      } )
      .then( html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString( html, 'text/html' );
        const newComponent = doc.querySelector( 'cart-component' );
        if ( !newComponent ) return;

        // Check if cart is now empty
        const newCartItems = newComponent.querySelectorAll( '[data-item-key]' );
        if ( newCartItems.length === 0 ) {
          // Cart is empty, replace entire component to show empty state
          this.replaceWith( newComponent.cloneNode( true ) );
          return;
        }

        // Replace all line items with fresh HTML from server
        const currentCartItems = this.querySelectorAll( '[data-item-key]' );

        currentCartItems.forEach( currentItem => {
          const variantId = currentItem.dataset.variantId;
          const itemKey = currentItem.dataset.itemKey;
          
          const newItem = variantId
            ? newComponent.querySelector( `[data-variant-id="${variantId}"]` )
            : newComponent.querySelector( `[data-item-key="${itemKey}"]` );

          if ( newItem ) {
            // Item still exists in cart, replace with updated version
            currentItem.replaceWith( newItem.cloneNode( true ) );
          } else {
            // Item was removed from cart, delete it from DOM
            currentItem.remove();
          }
        } );

        // Reinitialize input listeners for new elements
        this.initializeInputListeners();

        // Update summary section
        const newSummary = newComponent.querySelector( '[data-cart-summary]' );
        const currentSummary = this.querySelector( '[data-cart-summary]' );
        if ( newSummary && currentSummary ) {
          currentSummary.innerHTML = newSummary.innerHTML;
        }

        // Update cart count in header
        const updatedCartItems = this.querySelectorAll( '[data-item-key]' );
        this.updateCartCount( updatedCartItems.length );
      } )
      .catch( error => {
        console.error( 'Error refreshing cart component:', error );
      } );
  }

  checkEmptyCart ( cart ) {
    if ( cart.item_count === 0 ) {
      // Reload the page to show empty cart state
      window.location.reload();
    }
  }

  updateCartCount ( count ) {
    const cartCountElements = document.querySelectorAll( '[data-cart-count]' );
    cartCountElements.forEach( element => {
      element.textContent = count;
    } );
  }

  setItemLoading ( cartItem, isLoading ) {
    const quantityControls = cartItem.querySelector( '.quantity-controls' );
    const removeButton = cartItem.querySelector( '[data-remove-item]' );

    if ( isLoading ) {
      cartItem.style.opacity = '0.6';
      cartItem.style.pointerEvents = 'none';
    } else {
      cartItem.style.opacity = '';
      cartItem.style.pointerEvents = '';
    }
  }

  revertQuantity ( input, itemKey ) {
    // In a real implementation, you'd store the previous value
    // For now, we'll just reload the page as fallback
    console.warn( 'Reverting to page reload due to update failure' );
    setTimeout( () => window.location.reload(), 1000 );
  }

  showItemError ( cartItem, message ) {
    // Remove any existing error message for this item
    this.clearItemError( cartItem );

    // Create error message element
    const errorElement = document.createElement( 'div' );
    errorElement.className = 'cart-item-error col-span-2 text-red-600 text-xs p-2 bg-red-50 border border-red-200';
    errorElement.textContent = message;
    errorElement.setAttribute( 'data-cart-error', '' );

    // Insert after the cart item
    cartItem.appendChild( errorElement );

    // Auto-remove after 5 seconds
    setTimeout( () => {
      this.clearItemError( cartItem );
    }, 5000 );
  }

  clearItemError ( cartItem ) {
    const existingError = cartItem.querySelector( '[data-cart-error]' );
    if ( existingError ) {
      existingError.remove();
    }
  }

  showError ( message ) {
    // Create a simple error notification
    const notification = document.createElement( 'div' );
    notification.className = 'cart-error-notification fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded shadow-lg z-50';
    notification.textContent = message;
    document.body.appendChild( notification );

    setTimeout( () => {
      notification.remove();
    }, 5000 );
  }

  getCurrencyFromCart () {
    // Try to extract currency from existing price elements
    const priceElement = this.querySelector( '[data-line-total], [data-cart-subtotal]' );
    if ( priceElement && priceElement.textContent ) {
      const priceText = priceElement.textContent.trim();
      // Check for common currency symbols/codes
      if ( priceText.includes( '£' ) ) return 'GBP';
      if ( priceText.includes( '€' ) ) return 'EUR';
      if ( priceText.includes( '$' ) ) return 'USD';
      if ( priceText.includes( '¥' ) ) return 'JPY';
    }
    return 'GBP'; // Default to GBP as specified
  }

  formatMoney ( cents ) {
    if ( typeof cents === 'string' ) {
      cents = cents.replace( '.', '' );
    }

    const value = parseInt( cents, 10 );
    if ( isNaN( value ) ) {
      return this.currency === 'GBP' ? '£0.00' : '$0.00';
    }

    const dollars = Math.floor( value / 100 );
    const formattedPrice = dollars;

    // Return with appropriate currency symbol
    switch ( this.currency ) {
      case 'GBP':
        return `£${formattedPrice}`;
      case 'EUR':
        return `€${formattedPrice}`;
      case 'JPY':
        return `¥${Math.floor( value / 100 )}`; // JPY doesn't use decimal places
      case 'USD':
      default:
        return `$${formattedPrice}`;
    }
  }

  showRemoveConfirmation ( cartItem, quantityInput, newValue ) {
    // First, restore the original quantity until user confirms
    const originalValue = parseInt( quantityInput.dataset.originalValue ) || 1;
    quantityInput.value = originalValue;

    // If there's already an active confirmation dialog, remove it first
    if ( this.activeConfirmationDialog ) {
      this.activeConfirmationDialog.remove();
      this.activeConfirmationDialog = null;
    }

    // Create the confirmation overlay
    const confirmationDialog = document.createElement( 'div' );
    confirmationDialog.className = 'cart-remove-confirmation absolute -inset-x-5 -inset-y-px flex items-center justify-center bg-black/25 z-10';
    confirmationDialog.style.cssText = 'border-radius: inherit;';

    // Create the confirmation content
    const confirmationContent = document.createElement( 'div' );
    confirmationContent.className = 'bg-white p-4 max-w-sm w-full text-center';

    // Add confirmation message
    const message = document.createElement( 'p' );
    message.className = 'mb-3 text-gray-800';
    message.innerHTML = 'Remove item from your cart?';

    // Add buttons container
    const buttonsContainer = document.createElement( 'div' );
    buttonsContainer.className = 'flex justify-center space-x-2';

    // Add confirm button
    const confirmButton = document.createElement( 'button' );
    confirmButton.className = 'btn btn--primary';
    confirmButton.textContent = 'Remove';
    confirmButton.addEventListener( 'click', () => {
      // Remove the confirmation dialog
      confirmationDialog.remove();
      this.activeConfirmationDialog = null;

      // Proceed with removing the item
      quantityInput.value = newValue;
      this.updateQuantity( quantityInput );
    } );

    // Add cancel button
    const cancelButton = document.createElement( 'button' );
    cancelButton.className = 'btn btn--secondary';
    cancelButton.textContent = 'Cancel';
    cancelButton.addEventListener( 'click', () => {
      // Just remove the confirmation dialog
      confirmationDialog.remove();
      this.activeConfirmationDialog = null;

      // Restore the original value
      quantityInput.value = originalValue;
    } );

    // Assemble the confirmation dialog
    buttonsContainer.appendChild( cancelButton );
    buttonsContainer.appendChild( confirmButton );
    confirmationContent.appendChild( message );
    confirmationContent.appendChild( buttonsContainer );
    confirmationDialog.appendChild( confirmationContent );

    // Position the overlay relative to the cart item
    cartItem.style.position = 'relative';

    // Add to the DOM
    cartItem.appendChild( confirmationDialog );

    // Store reference to current dialog
    this.activeConfirmationDialog = confirmationDialog;

    // Focus the confirm button for keyboard accessibility
    setTimeout( () => confirmButton.focus(), 100 );
  }
}

// Define the custom element
customElements.define( 'cart-component', CartComponent );

// Export for potential external use
export default CartComponent;
